/**
 * sl-badk.2 + sl-badk.3 + sl-sffq.2: Syncs Y.js folder operations to PostgreSQL.
 *
 * Three sync mechanisms:
 * 1. Real-time doc placements: Subscribes to FolderStore.docLinkEvents$ and calls FolderApiService
 *    to keep the PostgreSQL FolderDoc membership table in sync.
 * 2. Real-time trash/restore: Subscribes to FolderStore.folderTrashEvents$ and calls
 *    FolderApiService to sync trash/restore to PostgreSQL (sl-sffq.2).
 * 3. Reconcile: Traverses the Y.js folder tree and syncs all doc placements to PostgreSQL.
 *    Also reconciles trashed status between Y.js and server (sl-sffq.2).
 *    Triggered (a) at service init when Y.js data finishes loading, and
 *    (b) after a folder role is granted (the folder now exists in PG).
 *
 * Only active for cloud workspaces (local workspaces have no folder RBAC).
 * Sync errors are logged and recover the server-derived navigation caches.
 */
import { DebugLogger } from '@affine/debug';
import { OnEvent, Service } from '@toeverything/infra';

import type { DocLinkEvent, FolderTrashEvent } from '../../organize';
import { AccessibleFolderCacheStore, FolderStore } from '../../organize';
import {
  type Workspace,
  WorkspaceInitialized,
  type WorkspaceService,
} from '../../workspace';
import type { FolderApiService } from './folder-api';
import type { FolderMutationOutboxService } from './folder-mutation-outbox';

const logger = new DebugLogger('FolderDocSync');

type OrphanFolderNode = {
  id: string;
  data: string;
  parentId?: string | null;
  trashed?: boolean | null;
};

function sortFoldersParentFirst(
  folders: OrphanFolderNode[]
): OrphanFolderNode[] {
  const remaining = new Map(folders.map(folder => [folder.id, folder]));
  const sorted: OrphanFolderNode[] = [];
  const sortedIds = new Set<string>();

  while (remaining.size > 0) {
    let progressed = false;
    for (const folder of [...remaining.values()]) {
      const parentId = folder.parentId ?? null;
      if (!parentId || !remaining.has(parentId) || sortedIds.has(parentId)) {
        sorted.push(folder);
        sortedIds.add(folder.id);
        remaining.delete(folder.id);
        progressed = true;
      }
    }

    if (!progressed) {
      sorted.push(
        ...[...remaining.values()].sort((a, b) => a.id.localeCompare(b.id))
      );
      break;
    }
  }

  return sorted;
}

/**
 * @OnEvent(WorkspaceInitialized) ensures the framework eagerly instantiates
 * this service when a workspace scope is created. Without it, the service
 * would remain lazy (never constructed) since no component depends on it.
 */
@OnEvent(
  WorkspaceInitialized,
  (s: FolderDocSyncService) => s.onWorkspaceInitialized
)
export class FolderDocSyncService extends Service {
  private reconciled = false;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly folderApiService: FolderApiService,
    private readonly folderMutationOutbox: FolderMutationOutboxService,
    private readonly folderStore: FolderStore,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore
  ) {
    super();
    this.subscribeToFolderEvents();
    this.subscribeToFolderTrashEvents();
    this.subscribeToFolderRoleGrants();
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    this.scheduleInitialReconcile();
  }

  private subscribeToFolderEvents(): void {
    const subscription = this.folderStore.docLinkEvents$.subscribe(event => {
      const workspace = this.workspaceService.workspace;
      if (!workspace || workspace.flavour === 'local') {
        return;
      }
      this.handleDocLinkEvent(workspace.id, event);
    });
    this.disposables.push(() => subscription.unsubscribe());
  }

  /**
   * sl-sffq.2: Subscribe to Y.js trash/restore events and sync to DB.
   * When a folder is trashed/restored in Y.js (local-first), fire the
   * corresponding GraphQL mutation to keep PostgreSQL in sync.
   */
  private subscribeToFolderTrashEvents(): void {
    const subscription = this.folderStore.folderTrashEvents$.subscribe(
      event => {
        const workspace = this.workspaceService.workspace;
        if (!workspace || workspace.flavour === 'local') {
          return;
        }
        this.handleTrashEvent(workspace.id, event);
      }
    );
    this.disposables.push(() => subscription.unsubscribe());
  }

  private subscribeToFolderRoleGrants(): void {
    const subscription = this.folderApiService.folderRoleGranted$.subscribe(
      ({ workspaceId }) => {
        this.reconcile(workspaceId);
      }
    );
    this.disposables.push(() => subscription.unsubscribe());
  }

  /**
   * Wait for Y.js folder data to finish loading, then run initial reconcile.
   * Catches drift from previous sessions where sync may have failed.
   */
  private scheduleInitialReconcile(): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') {
      return;
    }

    const subscription = this.folderStore
      .watchIsLoading()
      .subscribe((isLoading: boolean) => {
        if (!isLoading && !this.reconciled) {
          this.reconcile(workspace.id);
        }
      });
    this.disposables.push(() => subscription.unsubscribe());
  }

  /**
   * Traverse Y.js folder tree and sync all doc placements to PostgreSQL.
   * Also reconcile trashed status between Y.js and server (sl-sffq.2).
   * addDocToFolder is idempotent (upsert semantics) — safe to call repeatedly.
   * Errors are logged but never block (fire-and-forget).
   */
  private reconcile(workspaceId: string): void {
    this.reconciled = true;
    this.reconcileDocLinks(workspaceId);
    this.reconcileTrashedStatus(workspaceId);
  }

  private reconcileDocLinks(workspaceId: string): void {
    const allDocLinks = this.folderStore.getAllDocLinks();

    if (allDocLinks.length === 0) {
      return;
    }

    if (!this.accessibleFolderCache.hasCompletedServerFetch()) {
      logger.debug('reconcile: skipped doc links before server folder fetch', {
        total: allDocLinks.length,
        workspaceId,
      });
      return;
    }

    const serverFolders = this.accessibleFolderCache.getCachedFolders();
    if (serverFolders.length === 0) {
      return;
    }

    // Only replay server-confirmed doc placements. Old local Y.js entries can survive
    // a permanent delete locally; replaying them would recreate stale FolderDoc
    // rows on the server.
    const serverDocLinks =
      this.accessibleFolderCache.getCachedFolderDocLinkKeys();
    const docLinks = allDocLinks.filter(({ folderId, docId }) =>
      serverDocLinks.has(`${folderId}\u0000${docId}`)
    );

    logger.debug('reconcile: syncing doc links', {
      total: allDocLinks.length,
      filtered: docLinks.length,
      workspaceId,
    });

    for (const { folderId, docId } of docLinks) {
      this.folderMutationOutbox
        .enqueueAddDocToFolder(folderId, docId)
        .catch(err =>
          logger.debug('reconcile: sync skipped', { folderId, docId, err })
        );
    }
  }

  /**
   * sl-sffq.2 + sl-j66d: Reconcile trashed status between Y.js and server.
   *
   * Fetch server folder list (includes trashed status) and compare with Y.js:
   * - Server trashed=true, Y.js trashed=false → update Y.js to trashed
   * - Server trashed=false, Y.js trashed=true → update Y.js to not trashed
   * - Y.js folder not in server → check tombstone:
   *   - Tombstone exists → folder was permanently deleted → remove from Y.js
   *   - No tombstone → ghost folder (never synced) → sync to PG
   *
   * This handles the case where another client (or member via GraphQL)
   * trashed/restored/deleted a folder while this client was offline.
   */
  private reconcileTrashedStatus(workspaceId: string): void {
    // Get all server-confirmed folders from the accessible folder cache
    const serverFolders = this.accessibleFolderCache.getCachedFolders();
    if (serverFolders.length === 0) {
      // Cache not populated yet — skip reconciliation, will retry on next trigger
      return;
    }

    const serverFolderMap = new Map(serverFolders.map(f => [f.id, f]));

    // Get all Y.js folders (including trashed ones — we need to compare)
    const allYjsFolders = this.folderStore.getAllFolderNodes();

    let restoreSynced = 0;

    // Collect orphan folder IDs (in Y.js but not on server) for batch tombstone check
    const orphanFolders: OrphanFolderNode[] = [];

    for (const yjsFolder of allYjsFolders) {
      const serverFolder = serverFolderMap.get(yjsFolder.id);

      if (!serverFolder) {
        orphanFolders.push(yjsFolder);
        continue;
      }

      // Server folders from accessible cache are always non-trashed
      // (myAccessibleFolders excludes trashed).
      // If Y.js says trashed but server says accessible → was restored elsewhere
      if (yjsFolder.trashed) {
        this.folderStore.restoreFolderSilent(yjsFolder.id);
        restoreSynced++;
      }
    }

    // sl-j66d: Batch tombstone check for orphan folders
    if (orphanFolders.length > 0) {
      this.reconcileOrphanFolders(workspaceId, orphanFolders);
    }

    if (restoreSynced > 0) {
      logger.debug('reconcile: trashed status synced', {
        restoreSynced,
        orphanCount: orphanFolders.length,
        workspaceId,
      });
    }
  }

  /**
   * sl-j66d: Handle folders that exist in Y.js but not in the accessible folder cache.
   *
   * Three possible states for an orphan:
   * 1. Trashed on server (by another client/member) → sync trash to Y.js
   * 2. Permanently deleted (tombstone exists) → remove from Y.js
   * 3. Ghost folder (never synced to PG) → create in PG
   */
  private reconcileOrphanFolders(
    workspaceId: string,
    orphanFolders: OrphanFolderNode[]
  ): void {
    // Step 1: Fetch server-trashed folders to check if orphans are just trashed
    this.folderApiService
      .getTrashedFolders(workspaceId)
      .then(trashedFolders => {
        const serverTrashedMap = new Map(trashedFolders.map(f => [f.id, f]));

        // Separate orphans into server-trashed vs truly unknown
        const unknownOrphans: typeof orphanFolders = [];

        for (const folder of orphanFolders) {
          const serverTrashed = serverTrashedMap.get(folder.id);
          if (serverTrashed) {
            // Folder exists on server but is trashed → sync trash status to Y.js
            if (!folder.trashed) {
              logger.debug('reconcile: server-trashed folder synced to Y.js', {
                folderId: folder.id,
                name: folder.data,
              });
              this.folderStore.trashFolderSilent(folder.id);
            }
            // Already trashed in both → consistent, nothing to do
          } else {
            unknownOrphans.push(folder);
          }
        }

        // Step 2: For truly unknown orphans, check tombstones
        if (unknownOrphans.length === 0) return;

        const unknownIds = unknownOrphans.map(f => f.id);
        return this.folderApiService
          .checkDeletedFolderIds(workspaceId, unknownIds)
          .then(deletedIds => {
            const deletedSet = new Set(deletedIds);
            let cleaned = 0;
            const ghostFolders: typeof unknownOrphans = [];

            for (const folder of unknownOrphans) {
              if (deletedSet.has(folder.id)) {
                // Tombstone exists → permanently deleted → remove from Y.js
                logger.debug('reconcile: deleted folder cleaned from Y.js', {
                  folderId: folder.id,
                  name: folder.data,
                });
                this.folderStore.removeFolder(folder.id);
                cleaned++;
              } else {
                ghostFolders.push(folder);
              }
            }

            const synced = ghostFolders.length;
            this.syncGhostFoldersToServer(ghostFolders);
            if (cleaned > 0 || synced > 0) {
              logger.debug('reconcile: orphan folders processed', {
                cleaned,
                synced,
                workspaceId,
              });
            }
          });
      })
      .catch(err =>
        logger.error('reconcile: orphan folder processing failed', { err })
      );
  }

  private syncGhostFoldersToServer(folders: OrphanFolderNode[]): void {
    let chain = Promise.resolve();
    for (const folder of sortFoldersParentFirst(folders)) {
      chain = chain.then(async () => {
        logger.debug('reconcile: ghost folder syncing to PG', {
          folderId: folder.id,
          name: folder.data,
          parentId: folder.parentId ?? null,
        });
        await this.folderMutationOutbox.enqueueCreateFolder({
          folderId: folder.id,
          name: folder.data,
          parentId: folder.parentId ?? null,
        });
        if (folder.trashed) {
          await this.folderMutationOutbox.enqueueTrashFolder(folder.id);
        }
      });
    }
    chain.catch(err =>
      logger.debug('reconcile: ghost folder sync failed', { err })
    );
  }

  private handleDocLinkEvent(_workspaceId: string, event: DocLinkEvent): void {
    const recover = (message: string, err: unknown) => {
      this.recoverDocLinkSyncFailure(message, event, err);
    };

    switch (event.type) {
      case 'linked':
        // Optimistic: immediately remove doc from root cache to prevent
        // the doc appearing in both root and folder simultaneously.
        this.accessibleFolderCache.optimisticRemoveFromRoot(event.docId);
        this.folderMutationOutbox
          .enqueueAddDocToFolder(event.folderId, event.docId)
          .catch(err => recover('Sync linked failed', err));
        break;
      case 'unlinked':
        this.folderMutationOutbox
          .enqueueRemoveDocFromFolder(event.docId)
          .catch(err => recover('Sync unlinked failed', err));
        break;
      case 'moved':
        // Optimistic: immediately remove doc from root cache.
        this.accessibleFolderCache.optimisticRemoveFromRoot(event.docId);
        // Backend addDocToFolder has move semantics: deletes existing
        // link first, then creates new. One call is sufficient.
        this.folderMutationOutbox
          .enqueueAddDocToFolder(event.newFolderId, event.docId)
          .catch(err => recover('Sync moved failed', err));
        break;
    }
  }

  private recoverDocLinkSyncFailure(
    message: string,
    event: DocLinkEvent,
    err: unknown
  ): void {
    logger.error(message, { event, err });
    void Promise.allSettled([
      this.accessibleFolderCache.fetchAccessibleFolders(),
      this.accessibleFolderCache.refreshRootFolderDocIds(),
    ]).then(results => {
      const failed = results.find(result => result.status === 'rejected');
      if (failed) {
        logger.error('Recovering doc-link navigation cache failed', {
          event,
          err: failed.reason,
        });
      }
    });
  }

  private handleTrashEvent(
    _workspaceId: string,
    event: FolderTrashEvent
  ): void {
    const request =
      event.type === 'trashed'
        ? this.folderMutationOutbox.enqueueTrashFolder(event.folderId)
        : this.folderMutationOutbox.enqueueRestoreFolder(event.folderId);
    request.catch(err =>
      logger.error('Queue folder lifecycle failed', { event, err })
    );
  }
}
